import { FC } from 'react';

import { twMerge } from 'tailwind-merge';

import { AUTH_GREEN_BUTTON_CLASSES } from '../../constants/Auth';
import { BaseButton } from '../Buttons/BaseButton';
import AuthShell from '../Auth/AuthShell';

interface IVerifyEmailScreenProps {
  /**
   * Address the verification link was sent to. Omit it when the address is
   * unknown — e.g. when Auth0 bounced the user back with `email_not_verified`
   * — and the copy falls back to "your email address".
   */
  email?: string;
  /**
   * Takes the user back to the form with their details intact. Omit it to hide
   * the "use a different address" link — there's nothing to go back to when
   * this is reached on its own rather than straight after the form.
   */
  onUseAnotherEmail?: () => void;
  /**
   * Retries the sign-in. Shown as "I've verified it — sign in" when the user
   * lands here from an Auth0 redirect rather than straight after registering.
   */
  onSignIn?: () => void;
}

const VerifyEmailScreen: FC<IVerifyEmailScreenProps> = ({ email, onSignIn }) => (
  <AuthShell
    heading={email ? 'Verify your email' : 'Verify your email to continue'}
    subheading="One more step before you can sign in."
  >
    <div className="flex flex-col items-center gap-6 text-center" data-testid="verify-email-screen">
      {/* <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#E6F4EF]">
        <EnvelopeIcon className="h-7 w-7 text-[#0BAA84]" />
      </span> */}

      <div className="flex flex-col gap-2">
        {email ? (
          <p className="text-sm text-[#4B5565]">
            We've sent a verification link to{' '}
            <span className="font-semibold text-[#121926]">{email}</span>. Open it to activate your
            account.
          </p>
        ) : (
          <p className="text-sm text-[#4B5565]">
            Your account isn't active yet. We sent a verification link to your email address when
            you signed up — open it, then sign in again.
          </p>
        )}
        <p className="text-[13px] text-[#697586]">
          Can't find it? Check your spam or junk folder — it can take a minute or two to arrive.
        </p>
      </div>

      {onSignIn && (
        <BaseButton
          className={twMerge('w-full text-white', AUTH_GREEN_BUTTON_CLASSES)}
          onClick={onSignIn}
        >
          I've verified it — sign in
        </BaseButton>
      )}
    </div>
  </AuthShell>
);

export default VerifyEmailScreen;
